import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { paths } from "@/types/api";

type LeadBody =
  paths["/api/v1/crm/leads"]["post"]["requestBody"]["content"]["application/json"];
type AccountBody =
  paths["/api/v1/crm/accounts"]["post"]["requestBody"]["content"]["application/json"];

type LeadUpdateBody =
  paths["/api/v1/crm/leads/{lead_id}"]["patch"]["requestBody"]["content"]["application/json"];
type AccountUpdateBody =
  paths["/api/v1/crm/accounts/{account_id}"]["patch"]["requestBody"]["content"]["application/json"];
type ContactBody =
  paths["/api/v1/crm/accounts/{account_id}/contacts"]["post"]["requestBody"]["content"]["application/json"];
type NoteBody =
  paths["/api/v1/crm/notes"]["post"]["requestBody"]["content"]["application/json"];

export const crmKeys = {
  lookups: (c?: string) => ["crm", "lookups", c ?? "all"] as const,
  leads: ["crm", "leads"] as const,
  lead: (id: string) => ["crm", "leads", id] as const,
  accounts: ["crm", "accounts"] as const,
  account: (id: string) => ["crm", "accounts", id] as const,
  notes: (t: string, id: string) => ["crm", "notes", t, id] as const,
  tasks: ["crm", "tasks"] as const,
  stats: ["crm", "stats"] as const,
};

export function useCrmLookups(category?: string) {
  return useQuery({
    queryKey: crmKeys.lookups(category),
    staleTime: 30 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/crm/lookups", {
        params: { query: category ? { category } : {} },
      });
      if (error || !data) throw new Error("Failed to load lookups");
      return data;
    },
  });
}

export function useLeads() {
  return useQuery({
    queryKey: crmKeys.leads,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/crm/leads", {});
      if (error || !data) throw new Error("Failed to load leads");
      return data;
    },
  });
}

export function useCreateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data, error } = await apiClient.POST("/api/v1/crm/leads", {
        body: body as LeadBody,
      });
      if (error || !data) throw new Error("Failed to create lead");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: crmKeys.leads });
      qc.invalidateQueries({ queryKey: crmKeys.stats });
    },
  });
}

export function useSetLeadStage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { leadId: string; leadTypeId: string }) => {
      const { data, error } = await apiClient.PATCH(
        "/api/v1/crm/leads/{lead_id}/stage",
        {
          params: { path: { lead_id: vars.leadId } },
          body: { lead_type_id: vars.leadTypeId },
        },
      );
      if (error || !data) throw new Error("Failed to move lead");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: crmKeys.leads });
      qc.invalidateQueries({ queryKey: crmKeys.stats });
    },
  });
}

export function useConvertLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (leadId: string) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/crm/leads/{lead_id}/convert",
        {
          params: { path: { lead_id: leadId } },
          body: { create_property: true },
        },
      );
      if (error || !data) throw new Error("Conversion failed");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: crmKeys.leads });
      qc.invalidateQueries({ queryKey: crmKeys.stats });
    },
  });
}

export function useAccounts() {
  return useQuery({
    queryKey: crmKeys.accounts,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/crm/accounts", {});
      if (error || !data) throw new Error("Failed to load accounts");
      return data;
    },
  });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      const { data, error } = await apiClient.POST("/api/v1/crm/accounts", {
        body: body as AccountBody,
      });
      if (error || !data) throw new Error("Failed to create account");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: crmKeys.accounts }),
  });
}

export function useCrmStats() {
  return useQuery({
    queryKey: crmKeys.stats,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/crm/stats", {});
      if (error || !data) throw new Error("Failed to load stats");
      return data;
    },
  });
}

export function useLead(id: string | undefined) {
  return useQuery({
    queryKey: id ? crmKeys.lead(id) : crmKeys.lead(""),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/crm/leads/{lead_id}", {
        params: { path: { lead_id: id! } },
      });
      if (error || !data) throw new Error("Failed to load lead");
      return data;
    },
  });
}

export function useUpdateLead(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: LeadUpdateBody) => {
      const { data, error } = await apiClient.PATCH("/api/v1/crm/leads/{lead_id}", {
        params: { path: { lead_id: id } },
        body,
      });
      if (error || !data) throw new Error("Failed to update lead");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: crmKeys.leads });
      qc.invalidateQueries({ queryKey: crmKeys.lead(id) });
      qc.invalidateQueries({ queryKey: crmKeys.stats });
      qc.invalidateQueries({ queryKey: ["crm", "activity", "lead", id] });
    },
  });
}

export function useAccount(id: string | undefined) {
  return useQuery({
    queryKey: id ? crmKeys.account(id) : crmKeys.account(""),
    enabled: Boolean(id),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/crm/accounts/{account_id}", {
        params: { path: { account_id: id! } },
      });
      if (error || !data) throw new Error("Failed to load account");
      return data;
    },
  });
}

export function useUpdateAccount(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AccountUpdateBody) => {
      const { data, error } = await apiClient.PATCH("/api/v1/crm/accounts/{account_id}", {
        params: { path: { account_id: id } },
        body,
      });
      if (error || !data) throw new Error("Failed to update account");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: crmKeys.accounts });
      qc.invalidateQueries({ queryKey: crmKeys.account(id) });
    },
  });
}

export function useAddAccountContact(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ContactBody) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/crm/accounts/{account_id}/contacts",
        { params: { path: { account_id: id } }, body },
      );
      if (error || !data) throw new Error("Failed to add contact");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: crmKeys.account(id) }),
  });
}

export function useNotes(subjectType: "lead" | "account", subjectId: string | undefined) {
  return useQuery({
    queryKey: subjectId ? crmKeys.notes(subjectType, subjectId) : crmKeys.notes(subjectType, ""),
    enabled: Boolean(subjectId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/crm/notes", {
        params: { query: { subject_type: subjectType, subject_id: subjectId! } },
      });
      if (error || !data) throw new Error("Failed to load notes");
      return data;
    },
  });
}

export function useActivity(subjectType: "lead" | "account", subjectId: string | undefined) {
  return useQuery({
    queryKey: subjectId
      ? (["crm", "activity", subjectType, subjectId] as const)
      : (["crm", "activity", subjectType, ""] as const),
    enabled: Boolean(subjectId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/crm/activity", {
        params: { query: { subject_type: subjectType, subject_id: subjectId! } },
      });
      if (error || !data) throw new Error("Failed to load activity");
      return data;
    },
  });
}

export function useAddNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: NoteBody) => {
      const { data, error } = await apiClient.POST("/api/v1/crm/notes", { body });
      if (error || !data) throw new Error("Failed to add note");
      return data;
    },
    onSuccess: (_d, body) => {
      qc.invalidateQueries({ queryKey: crmKeys.notes(body.subject_type, body.subject_id) });
      qc.invalidateQueries({ queryKey: crmKeys.tasks });
      qc.invalidateQueries({ queryKey: crmKeys.stats });
    },
  });
}

export function useCompleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (noteId: string) => {
      const { data, error } = await apiClient.PATCH("/api/v1/crm/notes/{note_id}/done", {
        params: { path: { note_id: noteId } },
      });
      if (error || !data) throw new Error("Failed to complete task");
      return data;
    },
    onSuccess: () => {
      // We don't know the subject here — refresh all note lists, tasks and stats.
      qc.invalidateQueries({ queryKey: ["crm", "notes"] });
      qc.invalidateQueries({ queryKey: crmKeys.tasks });
      qc.invalidateQueries({ queryKey: crmKeys.stats });
    },
  });
}

export function useOpenTasks() {
  return useQuery({
    queryKey: crmKeys.tasks,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/crm/tasks", {});
      if (error || !data) throw new Error("Failed to load tasks");
      return data;
    },
  });
}
